import { useCallback, useEffect, useState } from "react";
import { BadgePercent, Ban, LoaderCircle, Plus } from "lucide-react";
import {
  createAdminDiscount,
  fetchAdminDiscounts,
  formatAdminAmount,
  revokeAdminDiscount,
} from "./adminApi";

const MAX_DISCOUNT_RUPEES = 699;

function indiaToday() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function formatExpiry(value) {
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(new Date(`${value}T00:00:00+05:30`));
}

function formatCreatedAt(value) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : new Intl.DateTimeFormat("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(parsed);
}

function discountStatusClass(status) {
  return `admin-status is-${String(status).toLowerCase().replace(/[^a-z]+/g, "-")}`;
}

export default function DiscountCodesView({ onUnauthorized }) {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [selectedDiscountId, setSelectedDiscountId] = useState("");
  const [message, setMessage] = useState("");
  const [code, setCode] = useState("");
  const [amountRupees, setAmountRupees] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [scope, setScope] = useState("current");

  const loadDiscounts = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const loadedDiscounts = await fetchAdminDiscounts();
      setDiscounts(loadedDiscounts);
      setSelectedDiscountId((current) =>
        loadedDiscounts.some(
          (discount) => discount.id === current && discount.status !== "archived",
        )
          ? current
          : "",
      );
    } catch (error) {
      if (error?.status === 401) onUnauthorized();
      else setLoadError(error?.message ?? "Discount codes could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => {
    loadDiscounts();
  }, [loadDiscounts]);

  const submitDiscount = async (event) => {
    event.preventDefault();
    if (saving) return;

    const numericAmount = Number(amountRupees);
    const discountPerBookPaise = Math.round(numericAmount * 100);
    if (
      !Number.isFinite(numericAmount) ||
      numericAmount <= 0 ||
      numericAmount > MAX_DISCOUNT_RUPEES ||
      Math.abs(discountPerBookPaise / 100 - numericAmount) > 0.000001
    ) {
      setMessage("Enter a discount between ₹0.01 and ₹699 with at most two decimal places.");
      return;
    }

    setSaving(true);
    setMessage("");
    try {
      const result = await createAdminDiscount({
        code,
        discountPerBookPaise,
        expiresOn,
        appliesToAllSkus: scope === "all",
      });
      setCode("");
      setAmountRupees("");
      setExpiresOn("");
      setScope("current");
      setMessage(`${result.discount.code} was created.`);
      await loadDiscounts();
    } catch (error) {
      if (error?.status === 401) onUnauthorized();
      else setMessage(error?.message ?? "Discount code could not be created.");
    } finally {
      setSaving(false);
    }
  };

  const selectedDiscount = discounts.find(
    (discount) => discount.id === selectedDiscountId,
  );

  const revokeSelectedDiscount = async () => {
    if (!selectedDiscount || selectedDiscount.status === "archived" || revoking) return;
    if (!window.confirm(`Revoke ${selectedDiscount.code}? It will stop working immediately.`)) {
      return;
    }

    setRevoking(true);
    setMessage("");
    try {
      const result = await revokeAdminDiscount(selectedDiscount.id);
      setSelectedDiscountId("");
      setMessage(`${result.code} was revoked. Its name can now be reused.`);
      await loadDiscounts();
    } catch (error) {
      if (error?.status === 401) onUnauthorized();
      else setMessage(error?.message ?? "Discount code could not be revoked.");
    } finally {
      setRevoking(false);
    }
  };

  return (
    <section className="admin-discounts-shell" aria-labelledby="admin-discounts-title">
      <header className="admin-orders-heading admin-discounts-heading">
        <div>
          <p className="admin-console-eyebrow">Pricing controls</p>
          <h1 id="admin-discounts-title">Discount codes</h1>
        </div>
        <BadgePercent aria-hidden="true" />
      </header>

      <div className="admin-discounts-layout">
        <form className="admin-discount-form" onSubmit={submitDiscount}>
          <div className="admin-discount-form-heading">
            <div>
              <p className="admin-console-eyebrow">New code</p>
              <h2>Add discount</h2>
            </div>
            <Plus aria-hidden="true" />
          </div>

          <label>
            <span>Code</span>
            <input
              type="text"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              minLength={3}
              maxLength={32}
              pattern="[A-Za-z0-9]{3,32}"
              placeholder="LAUNCH25"
              autoComplete="off"
              required
            />
            <small>3–32 letters or numbers.</small>
          </label>

          <label>
            <span>Discount per book</span>
            <div className="admin-money-input">
              <span aria-hidden="true">₹</span>
              <input
                type="number"
                value={amountRupees}
                onChange={(event) => setAmountRupees(event.target.value)}
                min="0.01"
                max={MAX_DISCOUNT_RUPEES}
                step="0.01"
                placeholder="100"
                required
              />
            </div>
          </label>

          <label>
            <span>Valid through</span>
            <input
              type="date"
              value={expiresOn}
              min={indiaToday()}
              onChange={(event) => setExpiresOn(event.target.value)}
              required
            />
            <small>The code remains valid for the full date in India.</small>
          </label>

          <fieldset>
            <legend>Applies to</legend>
            <label>
              <input
                type="radio"
                name="discount-scope"
                value="current"
                checked={scope === "current"}
                onChange={(event) => setScope(event.target.value)}
              />
              Current hardcover
            </label>
            <label>
              <input
                type="radio"
                name="discount-scope"
                value="all"
                checked={scope === "all"}
                onChange={(event) => setScope(event.target.value)}
              />
              All SKUs
            </label>
          </fieldset>

          <button type="submit" disabled={saving}>
            {saving ? <LoaderCircle className="is-spinning" aria-hidden="true" /> : <Plus aria-hidden="true" />}
            {saving ? "Creating…" : "Create discount code"}
          </button>
          {message && <p className="admin-discount-message" role="status">{message}</p>}
        </form>

        <div className="admin-discounts-list">
          <div className="admin-discounts-list-heading">
            <div>
              <p className="admin-console-eyebrow">Existing codes</p>
              <h2>{discounts.length} total</h2>
            </div>
            <div className="admin-discount-list-actions">
              <button
                type="button"
                className="is-danger"
                disabled={!selectedDiscount || selectedDiscount.status === "archived" || revoking}
                onClick={revokeSelectedDiscount}
              >
                <Ban aria-hidden="true" />
                {revoking ? "Revoking…" : "Revoke selected"}
              </button>
              <button type="button" disabled={loading} onClick={loadDiscounts}>
                {loading ? "Loading…" : "Refresh"}
              </button>
            </div>
          </div>
          {loadError && discounts.length > 0 && (
            <p className="admin-discount-list-error" role="alert">{loadError}</p>
          )}

          <div className="admin-orders-table-wrap">
            <table className="admin-orders-table admin-discounts-table">
              <thead>
                <tr>
                  <th scope="col" aria-label="Select discount code" />
                  <th scope="col">Code</th>
                  <th scope="col">Created</th>
                  <th scope="col">Amount / book</th>
                  <th scope="col">Valid through</th>
                  <th scope="col">Applies to</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {discounts.map((discount) => (
                  <tr key={discount.id}>
                    <td>
                      <input
                        type="radio"
                        name="selected-discount"
                        checked={selectedDiscountId === discount.id}
                        disabled={discount.status === "archived"}
                        onChange={() => setSelectedDiscountId(discount.id)}
                        aria-label={`Select ${discount.code} created ${discount.createdAt}`}
                      />
                    </td>
                    <td><strong>{discount.code}</strong></td>
                    <td>{formatCreatedAt(discount.createdAt)}</td>
                    <td>{formatAdminAmount(discount.discountPerBookPaise)}</td>
                    <td>{formatExpiry(discount.expiresOn)}</td>
                    <td>{discount.appliesToAllSkus ? "All SKUs" : "Current hardcover"}</td>
                    <td>
                      <span className={discountStatusClass(discount.status)}>
                        {discount.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {!loading && !discounts.length && (
                  <tr>
                    <td className="admin-orders-empty" colSpan="7">
                      {loadError || "No discount codes yet."}
                    </td>
                  </tr>
                )}
                {loading && !discounts.length && (
                  <tr>
                    <td className="admin-orders-empty" colSpan="7">Loading discount codes…</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
